import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApi, useArchivo, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../formato';
import { usePuede } from '../permisos';

export interface Pieza {
  id: string;
  vehiculoId: string;
  vin: string;
  nombre: string;
  categoria: string;
  numeroParte: string | null;
  estado: string;
  siguientes: string[];
  grado: string | null;
  precioListaMxn: string;
  ubicacion: string | null;
  codigo: string;
  apartadaPara: string | null;
  actualizadoEn: string;
}
interface Ubicacion {
  id: string;
  codigo: string;
  rack: string;
  casillero: string;
  descripcion: string | null;
  piezas: number;
}
interface Conteo {
  ubicacion: string;
  esperadas: number;
  encontradas: number;
  faltantes: { codigo: string; nombre: string }[];
  sobrantes: { codigo: string; nombre: string; estado: string; ubicacion: string | null }[];
  desconocidos: string[];
}

export const ESTADO_PIEZA: Record<string, string> = {
  en_auto: 'En el auto',
  desmontada: 'Desmontada',
  en_inventario: 'En inventario',
  apartada: 'Apartada',
  vendida: 'Vendida',
  entregada: 'Entregada',
  desechada: 'Desechada',
};

/**
 * Lector de QR con la cámara del celular (BarcodeDetector de Chrome); si el navegador no lo
 * tiene, basta con teclear el código o usar un lector que escribe como teclado.
 */
function LectorQr({ alLeer }: { alLeer: (codigo: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [activo, setActivo] = useState(false);
  const disponible = typeof window !== 'undefined' && 'BarcodeDetector' in window;
  useEffect(() => {
    if (!activo) return;
    let flujo: MediaStream | null = null;
    let vivo = true;
    const Detector = (
      window as unknown as {
        BarcodeDetector: new (o: object) => {
          detect: (v: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
        };
      }
    ).BarcodeDetector;
    const detector = new Detector({ formats: ['qr_code'] });
    void navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(async (f) => {
        flujo = f;
        if (!video.current) return;
        video.current.srcObject = f;
        await video.current.play();
        while (vivo) {
          const [hallado] = await detector.detect(video.current).catch(() => []);
          if (hallado?.rawValue) {
            alLeer(hallado.rawValue.split('/').pop() ?? hallado.rawValue);
            setActivo(false);
            break;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      })
      .catch(() => setActivo(false));
    return () => {
      vivo = false;
      flujo?.getTracks().forEach((t) => t.stop());
    };
  }, [activo, alLeer]);
  if (!disponible) return null;
  return activo ? (
    <video
      ref={video}
      muted
      playsInline
      style={{ width: '100%', maxWidth: 360, borderRadius: 8 }}
    />
  ) : (
    <button type="button" className="boton" onClick={() => setActivo(true)}>
      Escanear QR
    </button>
  );
}

/** Acciones sobre una pieza: su siguiente estado (con ubicación o precio cuando hacen falta). */
function AccionesPieza({ p, alCambiar }: { p: Pieza; alCambiar: () => void }) {
  const api = useApi();
  const permitido = usePuede();
  const [error, setError] = useState<string | null>(null);
  const etiqueta = useArchivo(`/inventory/piezas/${p.id}/etiqueta.svg`);
  const cambiar = async (estado: string) => {
    setError(null);
    const cuerpo: Record<string, string> = { estado };
    if (estado === 'en_inventario') {
      const u = window.prompt('Ubicación (código del rack y casillero, p. ej. A-03)');
      if (!u) return;
      cuerpo['ubicacion'] = u;
    }
    if (estado === 'vendida') {
      const precio = window.prompt('Precio de venta en MXN', p.precioListaMxn);
      if (!precio) return;
      cuerpo['precioVentaMxn'] = precio;
    }
    try {
      await api('POST', `/inventory/piezas/${p.id}/estado`, cuerpo);
      alCambiar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      {permitido('piezas.estado') &&
        p.siguientes.map((s) => (
          <button key={s} className="boton" onClick={() => void cambiar(s)}>
            {ESTADO_PIEZA[s] ?? s}
          </button>
        ))}{' '}
      {etiqueta && (
        <a className="boton" href={etiqueta} download={`${p.codigo}.svg`}>
          Etiqueta QR
        </a>
      )}
      {error && <div className="error">{error}</div>}
    </>
  );
}

/** Piezas del yonke (modo «Patio y taller»): buscar, escanear, ubicar, apartar y vender. */
export function Piezas() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const estado = params.get('estado') ?? '';
  const buscar = params.get('buscar') ?? '';
  const consulta = new URLSearchParams({
    ...(estado && { estado }),
    ...(buscar.length >= 2 && { buscar }),
  }).toString();
  const lista = useConsulta<Pieza[]>(`/inventory/piezas${consulta ? `?${consulta}` : ''}`);
  const [hallada, setHallada] = useState<Pieza | null>(null);
  const [error, setError] = useState<string | null>(null);

  const porCodigo = async (codigo: string) => {
    setError(null);
    try {
      setHallada(await api<Pieza>('GET', `/inventory/piezas/codigo/${encodeURIComponent(codigo)}`));
    } catch (e) {
      setHallada(null);
      setError((e as Error).message);
    }
  };
  const recargar = () => {
    lista.recargar();
    if (hallada) void porCodigo(hallada.codigo);
  };

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Piezas</h1>
          <div className="subtitulo">
            Inventario de piezas por VIN, con su ubicación en el patio
          </div>
        </div>
        <div className="relleno" />
        <Link className="boton" to="/piezas/ubicaciones">
          Ubicaciones y conteo
        </Link>
      </div>

      <section className="seccion">
        <form
          className="cuerpo"
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          onSubmit={(e) => {
            e.preventDefault();
            const codigo = String(new FormData(e.currentTarget).get('codigo') ?? '').trim();
            if (codigo) void porCodigo(codigo);
          }}
        >
          <input name="codigo" placeholder="Código de la etiqueta (P-…)" aria-label="Código" />
          <button className="boton primario">Buscar código</button>
          <LectorQr alLeer={(c) => void porCodigo(c)} />
        </form>
        {error && <p className="error">{error}</p>}
        {hallada && (
          <div className="cuerpo" style={{ borderTop: '1px solid var(--borde-suave)' }}>
            <strong>{hallada.nombre}</strong> · {ESTADO_PIEZA[hallada.estado]} ·{' '}
            {hallada.ubicacion ?? 'sin ubicación'} · {mxn(hallada.precioListaMxn)}
            <div className="vin">
              {hallada.codigo} · <Link to={`/vehiculos/${hallada.vehiculoId}`}>{hallada.vin}</Link>
            </div>
            <AccionesPieza p={hallada} alCambiar={recargar} />
          </div>
        )}
      </section>

      <section className="seccion">
        <header style={{ gap: 8, flexWrap: 'wrap' }}>
          <h2>Todas</h2>
          <div style={{ flex: 1 }} />
          <input
            placeholder="Buscar por nombre o número de parte"
            defaultValue={buscar}
            aria-label="Buscar"
            onChange={(e) => {
              const p = new URLSearchParams(params);
              if (e.target.value) p.set('buscar', e.target.value);
              else p.delete('buscar');
              setParams(p, { replace: true });
            }}
          />
          <select
            value={estado}
            aria-label="Estado"
            onChange={(e) => {
              const p = new URLSearchParams(params);
              if (e.target.value) p.set('estado', e.target.value);
              else p.delete('estado');
              setParams(p);
            }}
          >
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_PIEZA).map(([c, t]) => (
              <option key={c} value={c}>
                {t}
              </option>
            ))}
          </select>
        </header>
        {lista.error && <p className="error">{lista.error}</p>}
        {lista.datos && !lista.datos.length ? (
          <p className="vacio">
            No hay piezas. Se crean al desarmar un vehículo (ficha del vehículo › Piezas).
          </p>
        ) : (
          <table>
            <tbody>
              {(lista.datos ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.nombre}
                    <div className="vin">
                      {p.codigo} · {p.categoria}
                      {p.numeroParte ? ` · ${p.numeroParte}` : ''} ·{' '}
                      <Link to={`/vehiculos/${p.vehiculoId}`}>{p.vin}</Link>
                    </div>
                  </td>
                  <td>
                    <span className="etiqueta">{ESTADO_PIEZA[p.estado] ?? p.estado}</span>
                    <div className="vin">{p.ubicacion ?? '—'}</div>
                  </td>
                  <td className="num">
                    {mxn(p.precioListaMxn)}
                    <div className="vin">{fechaCorta(p.actualizadoEn)}</div>
                  </td>
                  <td className="num">
                    <AccionesPieza p={p} alCambiar={recargar} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Ubicaciones del patio y conteo cíclico por ubicación (escaneando o tecleando códigos). */
export function Ubicaciones() {
  const api = useApi();
  const permitido = usePuede();
  const lista = useConsulta<Ubicacion[]>('/inventory/ubicaciones');
  const [error, setError] = useState<string | null>(null);
  const [conteo, setConteo] = useState<Conteo | null>(null);
  const [leidos, setLeidos] = useState<string[]>([]);
  const [ubicacion, setUbicacion] = useState('');

  const crear = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const d = new FormData(forma);
    setError(null);
    try {
      await api('POST', '/inventory/ubicaciones', {
        rack: d.get('rack'),
        casillero: d.get('casillero'),
        ...(d.get('descripcion') && { descripcion: d.get('descripcion') }),
      });
      forma.reset();
      lista.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const contar = async () => {
    setError(null);
    try {
      setConteo(
        await api<Conteo>(
          'POST',
          `/inventory/ubicaciones/${encodeURIComponent(ubicacion)}/conteo`,
          {
            codigos: leidos,
          },
        ),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/piezas">Piezas</Link> ›
          </div>
          <h1>Ubicaciones y conteo</h1>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        <header>
          <h2>Ubicaciones</h2>
        </header>
        <table>
          <tbody>
            {(lista.datos ?? []).map((u) => (
              <tr key={u.id}>
                <td>
                  <strong>{u.codigo}</strong>
                  <div className="vin">
                    {u.descripcion ?? `Rack ${u.rack}, casillero ${u.casillero}`}
                  </div>
                </td>
                <td className="num">{u.piezas} piezas</td>
              </tr>
            ))}
          </tbody>
        </table>
        {permitido('piezas.administrar') && (
          <form
            className="cuerpo formulario"
            onSubmit={(e) => void crear(e)}
            style={{ borderTop: '1px solid var(--borde-suave)' }}
          >
            <label className="campo">
              <span>Rack</span>
              <input name="rack" required pattern="[A-Za-z0-9]{1,8}" />
            </label>
            <label className="campo">
              <span>Casillero</span>
              <input name="casillero" required pattern="[A-Za-z0-9]{1,8}" />
            </label>
            <label className="campo">
              <span>Descripción</span>
              <input name="descripcion" maxLength={200} />
            </label>
            <div>
              <button className="boton">Agregar ubicación</button>
            </div>
          </form>
        )}
      </section>

      {permitido('piezas.administrar') && (
        <section className="seccion">
          <header>
            <h2>Conteo cíclico</h2>
          </header>
          <div className="cuerpo formulario">
            <label className="campo">
              <span>Ubicación</span>
              <select value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
                <option value="">Elige…</option>
                {(lista.datos ?? []).map((u) => (
                  <option key={u.id} value={u.codigo}>
                    {u.codigo}
                  </option>
                ))}
              </select>
            </label>
            <form
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
              onSubmit={(e) => {
                e.preventDefault();
                const campo = e.currentTarget.elements.namedItem('codigo') as HTMLInputElement;
                const c = campo.value.trim().toUpperCase();
                if (c && !leidos.includes(c)) setLeidos([...leidos, c]);
                campo.value = '';
              }}
            >
              <input name="codigo" placeholder="Código leído" aria-label="Código leído" />
              <button className="boton">Agregar</button>
              <LectorQr
                alLeer={(c) => setLeidos((l) => (l.includes(c) ? l : [...l, c.toUpperCase()]))}
              />
            </form>
            <p className="subtitulo">
              {leidos.length} códigos leídos: {leidos.join(', ') || '—'}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="boton primario"
                disabled={!ubicacion}
                onClick={() => void contar()}
              >
                Comparar con el sistema
              </button>
              <button className="boton" onClick={() => setLeidos([])}>
                Limpiar
              </button>
            </div>
            {conteo && (
              <div>
                <p>
                  <strong>
                    {conteo.encontradas} de {conteo.esperadas}
                  </strong>{' '}
                  piezas encontradas en {conteo.ubicacion}.
                </p>
                {conteo.faltantes.length > 0 && (
                  <p className="error">
                    Faltan: {conteo.faltantes.map((f) => `${f.codigo} ${f.nombre}`).join(', ')}
                  </p>
                )}
                {conteo.sobrantes.length > 0 && (
                  <p className="etiqueta aviso">
                    Están aquí pero el sistema las tiene en otro lado:{' '}
                    {conteo.sobrantes
                      .map((s) => `${s.codigo} ${s.nombre} (${s.ubicacion ?? s.estado})`)
                      .join(', ')}
                  </p>
                )}
                {conteo.desconocidos.length > 0 && (
                  <p className="subtitulo">
                    Códigos desconocidos: {conteo.desconocidos.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
